import classNames from "classnames";
import { FC, memo, useCallback, useEffect, useRef, useState } from "react";

import { BaseOption } from "entities/common";

import { ReactComponent as ArrowSVG } from "assets/images/common/arrow.svg";

import { VALIDATION_CLASS } from "utils/constants";

type PropTypes = {
  label?: string;
  error?: string;
  hint?: string;
  variant?: "primary" | "secondary";
  isRequired?: boolean;
  isDisabled?: boolean;
  options: BaseOption[];
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
  className?: string;
};

const Select: FC<PropTypes> = ({
  label,
  error,
  hint,
  variant = "primary",
  isRequired,
  isDisabled,
  options,
  placeholder,
  value,
  onChange,
  className,
}) => {
  const [isOpen, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleToggle = useCallback(() => {
    if (!isDisabled) setOpen((prev) => !prev);
  }, [isDisabled]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setOpen(false);
    },
    [onChange],
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") setOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={ref}
      className={classNames("select", className, `select--${variant}`, {
        "select--disabled": isDisabled,
      })}
      onKeyDown={handleKeyDown}
    >
      <div
        className={classNames("select__field", {
          "select__field--filled": !!selectedOption,
          "select__field--open": isOpen,
          "select__field--error": !!error,
          [VALIDATION_CLASS]: !!error,
        })}
        onClick={handleToggle}
        role="combobox"
        aria-expanded={isOpen}
        tabIndex={isDisabled ? -1 : 0}
        title={selectedOption ? selectedOption.label : placeholder || ""}
      >
        <span className="select__value">{selectedOption ? selectedOption.label : placeholder || ""}</span>

        {!!label && (
          <span
            className={classNames("select__label", {
              "select__label--required": isRequired,
            })}
          >
            {label}
          </span>
        )}

        <span className={classNames("select__arrow", { "select__arrow--open": isOpen })}>
          <ArrowSVG />
        </span>
      </div>

      {isOpen && (
        <ul className="select__dropdown">
          {options.map((option) => (
            <li
              key={option.value}
              className={classNames("select__option", {
                "select__option--selected": option.value === value,
                "select__option--disabled": option.disabled,
              })}
              onClick={() => !option.disabled && handleSelect(option.value)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}

      {!!error && <div className="select__error">{error}</div>}
      {!!hint && !error && <div className="select__hint">{hint}</div>}
    </div>
  );
};

export default memo(Select);
